import React from "react";
import { Navigate } from "react-router-dom";
import { LoginRedirect } from "./LoginRedirect";
import { Shield, Lock, AlertTriangle } from "lucide-react";
import { useAuth } from "../hooks/useAuth";

type RequiredRole = "platform_admin" | "super_admin" | "admin" | "member";

interface RoleGuardProps {
  /** The minimum role required to access the wrapped content. */
  requiredRole: RequiredRole;
  /** Content to render when the user has the required role. */
  children: React.ReactNode;
  /** If true, redirects unauthorized users to "/" instead of showing access denied. */
  redirectOnDeny?: boolean;
  /** Custom message for the access denied screen. */
  denyMessage?: string;
}

/**
 * Role-based route guard that enforces the 3-tier access hierarchy:
 *   platform_admin > super_admin > admin > member
 *
 * Usage:
 *   <RoleGuard requiredRole="platform_admin">
 *     <TeamPage />
 *   </RoleGuard>
 */
export function RoleGuard({
  requiredRole,
  children,
  redirectOnDeny = false,
  denyMessage,
}: RoleGuardProps) {
  const { loading, isPlatformAdmin, orgRole, user } = useAuth();

  // ── Loading ────────────────────────────────────────────────
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          padding: "var(--spacing-3xl)",
        }}
      >
        <div
          className="spinner"
          style={{
            width: 32,
            height: 32,
            border: "3px solid var(--border-color)",
            borderTopColor: "var(--color-primary)",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
          }}
        />
      </div>
    );
  }

  // ── Not authenticated ──────────────────────────────────────
  if (!user) {
    return <LoginRedirect />;
  }

  // ── Authorization check ────────────────────────────────────
  const hasAccess = checkAccess(requiredRole, isPlatformAdmin, orgRole);

  if (hasAccess) {
    return <>{children}</>;
  }

  // ── Denied ─────────────────────────────────────────────────
  if (redirectOnDeny) {
    return <Navigate to="/" replace />;
  }

  return <AccessDeniedScreen requiredRole={requiredRole} message={denyMessage} />;
}

// ── Helpers ──────────────────────────────────────────────────

const ROLE_HIERARCHY: Record<string, number> = {
  platform_admin: 100,
  super_admin: 80,
  admin: 60,
  member: 20,
};

function checkAccess(
  requiredRole: RequiredRole,
  isPlatformAdmin: boolean,
  orgRole: string | null
): boolean {
  // Platform admin bypasses everything
  if (isPlatformAdmin) return true;

  // For platform_admin-only routes, non-platform users are always denied
  if (requiredRole === "platform_admin") return false;

  // For org-level roles, check hierarchy
  const requiredLevel = ROLE_HIERARCHY[requiredRole] ?? 0;
  const userLevel = ROLE_HIERARCHY[orgRole ?? ""] ?? 0;

  return userLevel >= requiredLevel;
}

// ── Access Denied UI ─────────────────────────────────────────

function AccessDeniedScreen({
  requiredRole,
  message,
}: {
  requiredRole: RequiredRole;
  message?: string;
}) {
  const icon =
    requiredRole === "platform_admin" ? (
      <Shield size={36} strokeWidth={1.5} />
    ) : requiredRole === "super_admin" ? (
      <AlertTriangle size={36} strokeWidth={1.5} />
    ) : (
      <Lock size={36} strokeWidth={1.5} />
    );

  const defaultMessage =
    requiredRole === "platform_admin"
      ? "This area is restricted to platform administrators. Contact your system administrator if you need access."
      : `You need at least ${requiredRole} permissions to access this section.`;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
        padding: "var(--spacing-3xl)",
        background: "var(--bg-secondary)",
        borderRadius: "var(--border-radius-lg)",
        border: "1px solid var(--border-color)",
        margin: "var(--spacing-xl)",
      }}
    >
      <div
        style={{
          background:
            "linear-gradient(135deg, rgba(239,68,68,0.12), rgba(239,68,68,0.04))",
          padding: "var(--spacing-lg)",
          borderRadius: "50%",
          marginBottom: "var(--spacing-lg)",
          color: "#ef4444",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {icon}
      </div>

      <h2
        style={{
          margin: "0 0 var(--spacing-sm)",
          color: "var(--text-primary)",
          fontSize: "var(--font-size-xl)",
          fontWeight: 600,
        }}
      >
        Access Restricted
      </h2>
      <p
        style={{
          color: "var(--text-muted)",
          marginBottom: "var(--spacing-xl)",
          textAlign: "center",
          maxWidth: 420,
          lineHeight: 1.6,
        }}
      >
        {message || defaultMessage}
      </p>
    </div>
  );
}
